import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';

export type SyncDataType = 'transactions' | 'expenses' | 'counterparties';

interface SyncProgress {
  status: 'idle' | 'running' | 'completed' | 'failed' | 'partial' | 'not_running' | 'stopped';
  current_week?: {
    week_number: number;
    total_weeks: number;
    start_date: string;
    end_date: string;
  };
  completed_weeks?: Array<{
    week_number: number;
    start_date: string;
    end_date: string;
    transaction_count?: number;
    expense_count?: number;
  }>;
  failed_weeks?: Array<{
    week_number: number;
    start_date: string;
    end_date: string;
    error?: string;
  }>;
  total_weeks?: number;
  progress_percentage?: number;
  total_transactions?: number;
  total_expenses?: number;
  elapsed_seconds?: number;
  start_time?: string;
  error?: string;
  sync_type?: 'historical' | 'incremental';
  from_timestamp?: string;
  detected_empty_db?: boolean;
  message?: string;
  /** True while waiting on a long single HTTP request (e.g. /sync-complete); no week-based % */
  indeterminate?: boolean;
}

interface UseUnifiedSyncOptions {
  dataType: SyncDataType;
  account?: string;
  autoSyncOnMount?: boolean;
  autoSyncDelay?: number; // Delay in milliseconds before auto-sync
  /** Called after sync POST succeeds (e.g. to surface failed_accounts / partial sync). */
  onSyncStarted?: (responseData: Record<string, unknown>) => void;
}

interface UseUnifiedSyncReturn {
  syncStatus: SyncProgress['status'];
  syncProgress: SyncProgress | null;
  isSyncing: boolean;
  syncType: 'historical' | 'incremental' | null;
  startSync: () => Promise<void>;
  stopSync: () => Promise<void>;
  error: string | null;
}

export function useUnifiedSync({
  dataType,
  account = 'ireland',
  autoSyncOnMount = true,
  autoSyncDelay = 500,
  onSyncStarted
}: UseUnifiedSyncOptions): UseUnifiedSyncReturn {
  const { user } = useAuth();
  const [syncStatus, setSyncStatus] = useState<SyncProgress['status']>('idle');
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncType, setSyncType] = useState<'historical' | 'incremental' | null>(null);
  
  const progressPollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartedRef = useRef<boolean>(false);
  const autoSyncTriggeredRef = useRef<boolean>(false);

  // Determine API endpoints based on data type
  const getEndpoints = useCallback(() => {
    switch (dataType) {
      case 'transactions':
        return {
          // Use direct incremental sync for transactions so "Sync Transactions"
          // reflects latest Revolut API data immediately.
          sync: '/api/revolut/sync-complete',
          progress: null
        };
      case 'expenses':
        return {
          sync: '/api/revolut/expenses/sync-historical',
          progress: '/api/revolut/expenses/sync-historical/progress'
        };
      case 'counterparties':
        return {
          sync: '/api/revolut/counterparties/sync',
          progress: null // Counterparties sync is synchronous, no progress endpoint
        };
      default:
        throw new Error(`Unknown data type: ${dataType}`);
    }
  }, [dataType]);

  // Poll for sync progress
  const pollSyncProgress = useCallback(async () => {
    const endpoints = getEndpoints();
    if (!endpoints.progress) {
      // No progress endpoint (e.g., counterparties) - stop polling
      pollingStartedRef.current = false;
      if (progressPollIntervalRef.current) {
        clearInterval(progressPollIntervalRef.current);
        progressPollIntervalRef.current = null;
      }
      return;
    }

    try {
      const response = await api.get(endpoints.progress, {
        timeout: 30000
      });
      
      const progressData = response.data;
      setSyncProgress(progressData);
      setSyncStatus(progressData.status);
      
      // Update sync type if available
      if (progressData.sync_type) {
        setSyncType(progressData.sync_type);
      }
      
      // If sync is completed, failed, or not running, stop polling
      if (
        progressData.status === 'completed' ||
        progressData.status === 'failed' ||
        progressData.status === 'not_running'
      ) {
        pollingStartedRef.current = false;
        if (progressPollIntervalRef.current) {
          clearInterval(progressPollIntervalRef.current);
          progressPollIntervalRef.current = null;
        }
        
        if (progressData.status === 'failed' && progressData.error) {
          setError(progressData.error);
        }
      }
    } catch (err: any) {
      // Handle 401 errors gracefully - token expired but sync continues in background
      if (err.response?.status === 401) {
        console.warn('Token expired during sync progress check - sync continues in background');
        return; // Continue polling
      }
      
      console.error('Error polling sync progress:', err);
      
      // If we get multiple consecutive errors, stop polling
      if (err.code === 'ECONNABORTED' || err.response?.status >= 500) {
        const errorCount = (err as any).errorCount || 0;
        if (errorCount >= 5) {
          console.warn('Stopping progress polling due to repeated errors');
          pollingStartedRef.current = false;
          if (progressPollIntervalRef.current) {
            clearInterval(progressPollIntervalRef.current);
            progressPollIntervalRef.current = null;
          }
          setError('Failed to fetch progress after multiple attempts. Sync may still be running in the background.');
        } else {
          (err as any).errorCount = errorCount + 1;
        }
      }
    }
  }, [getEndpoints]);

  // Start polling progress
  const startProgressPolling = useCallback(() => {
    if (pollingStartedRef.current) {
      console.log('⚠️ Polling already started, skipping duplicate start');
      return;
    }
    
    pollingStartedRef.current = true;
    
    // Clear any existing interval
    if (progressPollIntervalRef.current) {
      clearInterval(progressPollIntervalRef.current);
      progressPollIntervalRef.current = null;
    }
    
    // Poll immediately
    pollSyncProgress();
    
    // Then poll every 5 seconds
    progressPollIntervalRef.current = setInterval(() => {
      pollSyncProgress();
    }, 5000);
  }, [pollSyncProgress]);

  // Stop polling progress
  const stopProgressPolling = useCallback(() => {
    pollingStartedRef.current = false;
    if (progressPollIntervalRef.current) {
      clearInterval(progressPollIntervalRef.current);
      progressPollIntervalRef.current = null;
    }
  }, []);

  // Start sync
  const startSync = useCallback(async () => {
    if (!user?.tx_import) {
      const msg =
        'Transaction import is disabled for your account. Ask an owner to grant the tx_import capability.';
      setError(msg);
      setSyncStatus('failed');
      setSyncProgress({
        status: 'failed',
        error: msg,
      });
      return;
    }
    const endpoints = getEndpoints();
    
    try {
      setError(null);
      setSyncStatus('running');
      setSyncProgress({
        status: 'running',
        sync_type: dataType === 'transactions' ? 'incremental' : 'historical',
        message:
          dataType === 'transactions'
            ? 'Contacting Revolut for all accounts (Ireland + UK). This often takes 1–5 minutes; step-by-step % is not available for this API.'
            : `Starting ${dataType} sync...`,
        indeterminate: dataType === 'transactions',
      });
      
      // Reset polling flag
      pollingStartedRef.current = false;
      
      console.log(`🚀 Starting unified ${dataType} sync...`);
      console.log(`🚀 Sync endpoint: ${endpoints.sync}`);
      console.log(`🚀 Sync account: ${account}`);
      
      // Start the sync
      const syncBody: Record<string, unknown> = { account };
      // Admin/owner: ask backend to echo a capped summary of raw Revolut rows (for DevTools / verification).
      if (dataType === 'transactions' && (user?.role === 'admin' || user?.role === 'owner')) {
        syncBody.include_api_preview = true;
        syncBody.api_preview_limit = 200;
      }
      const response = await api.post(endpoints.sync, syncBody, {
        // /sync-complete can run several minutes (two accounts, cursor pagination, Revolut latency).
        timeout: dataType === 'transactions' ? 600000 : 90000,
      });
      
      console.log(`🚀 Sync POST response:`, response.data);

      const hasProgressEndpoint = Boolean(endpoints.progress);
      if (response.data.success && (response.data.status === 'running' || response.data.sync_type)) {
        console.log(`✅ Unified ${dataType} sync started successfully:`, response.data);
        onSyncStarted?.(response.data as Record<string, unknown>);

        // Update sync type from response
        if (response.data.sync_type) {
          setSyncType(response.data.sync_type);
        }

        // If there's a progress endpoint, start polling
        if (endpoints.progress) {
          startProgressPolling();
        } else {
          // Synchronous sync (e.g., counterparties) - check response for completion
          if (response.data.success) {
            setSyncStatus('completed');
            setSyncProgress({
              status: 'completed',
              sync_type: response.data.sync_type || 'incremental',
              indeterminate: false,
            });
          }
        }
      } else if (!hasProgressEndpoint) {
        // Synchronous endpoints (e.g. transactions via /sync-complete) return
        // completion payload directly, often without `success`.
        onSyncStarted?.(response.data as Record<string, unknown>);
        const errText = typeof response.data?.error === 'string' ? response.data.error.trim() : '';
        const hasErrorMessage = errText.length > 0;
        const imported = Number((response.data as any)?.imported_count ?? 0);
        const updated = Number((response.data as any)?.updated_count ?? 0);
        const totalProcessed = Number((response.data as any)?.total_processed ?? imported + updated);
        const fetched = Number((response.data as any)?.revolut_rows_fetched ?? 0);
        const skippedPending = Number((response.data as any)?.skipped_pending ?? 0);

        // Backend sometimes returns HTTP 200 with an `error` string when nothing changed.
        // Treat that as a failed sync so the UI doesn't look "green" while doing nothing.
        const looksLikeNoopFailure =
          hasErrorMessage &&
          (errText.toLowerCase().includes('no transactions were processed') ||
            errText.toLowerCase().includes('sync completed but no transactions'));

        const status: SyncProgress['status'] = looksLikeNoopFailure ? 'failed' : hasErrorMessage ? 'partial' : 'completed';
        setSyncStatus(status);
        setSyncType((response.data?.is_incremental ? 'incremental' : 'historical') as 'historical' | 'incremental');
        setSyncProgress({
          status,
          sync_type: (response.data?.is_incremental ? 'incremental' : 'historical') as 'historical' | 'incremental',
          indeterminate: false,
          message:
            response.data?.details ||
            response.data?.message ||
            response.data?.error ||
            `Completed ${dataType} sync`,
          total_transactions:
            Number.isFinite(totalProcessed) && totalProcessed > 0
              ? totalProcessed
              : Number.isFinite(fetched) && fetched > 0
                ? fetched
                : undefined,
          error: hasErrorMessage ? response.data.error : undefined,
        });
      } else {
        throw new Error(response.data.error || `Unified ${dataType} sync failed to start`);
      }
    } catch (err: any) {
      console.error(`❌ Unified ${dataType} sync failed:`, err);
      const data = err.response?.data;
      let message =
        data?.error || err.message || `Failed to sync ${dataType}`;
      const failed = data?.failed_accounts;
      if (Array.isArray(failed) && failed.length) {
        const detail = failed
          .map((f: { account?: string; error?: string }) =>
            f?.account ? `${f.account}: ${f.error || 'failed'}` : f?.error
          )
          .filter(Boolean)
          .join(' · ');
        if (detail) {
          message = `${message} — ${detail}`;
        }
      }
      setError(message);
      setSyncStatus('failed');
      setSyncProgress({
        status: 'failed',
        error: message
      });
    }
  }, [dataType, account, getEndpoints, startProgressPolling, onSyncStarted, user?.role, user?.tx_import]);

  // Stop sync
  const stopSync = useCallback(async () => {
    const endpoints = getEndpoints();
    
    try {
      console.log(`🛑 Stopping ${dataType} sync...`);
      
      // Only transactions has stop endpoint implemented
      if (dataType === 'transactions') {
        const response = await api.post('/api/revolut/sync-historical/stop', {}, {
          timeout: 10000
        });
        
        if (response.data.success) {
          console.log(`✅ ${dataType} sync stopped successfully`);
          setSyncStatus('stopped');
          setSyncProgress({
            status: 'stopped',
            message: 'Sync stopped by user'
          });
          stopProgressPolling();
        } else {
          throw new Error(response.data.message || `Failed to stop ${dataType} sync`);
        }
      } else if (dataType === 'expenses') {
        const response = await api.post('/api/revolut/expenses/sync-historical/stop', {}, {
          timeout: 10000
        });
        
        if (response.data.success) {
          console.log(`✅ ${dataType} sync stopped successfully`);
          setSyncStatus('stopped');
          setSyncProgress({
            status: 'stopped',
            message: 'Sync stopped by user'
          });
          stopProgressPolling();
        } else {
          throw new Error(response.data.message || `Failed to stop ${dataType} sync`);
        }
      } else {
        console.warn(`Stop sync not supported for ${dataType}`);
        // For counterparties, just stop polling
        stopProgressPolling();
      }
    } catch (err: any) {
      console.error(`❌ Failed to stop ${dataType} sync:`, err);
      setError(err.response?.data?.error || err.message || `Failed to stop ${dataType} sync`);
    }
  }, [dataType, getEndpoints, stopProgressPolling]);

  // Auto-sync on mount
  useEffect(() => {
    console.log(`🔍 Auto-sync check: autoSyncOnMount=${autoSyncOnMount}, autoSyncTriggeredRef.current=${autoSyncTriggeredRef.current}`);
    if (!user?.tx_import) {
      console.log('⏭️ Auto-sync skipped: missing tx_import capability');
      return;
    }
    if (autoSyncOnMount && !autoSyncTriggeredRef.current) {
      autoSyncTriggeredRef.current = true;
      console.log(`✅ Auto-sync triggered for ${dataType} - will start in ${autoSyncDelay}ms`);
      
      // Delay auto-sync slightly to avoid blocking UI
      const timeoutId = setTimeout(() => {
        console.log(`⏰ Auto-sync timeout fired - calling startSync()`);
        startSync();
      }, autoSyncDelay);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      console.log(`⏭️ Auto-sync skipped: autoSyncOnMount=${autoSyncOnMount}, already triggered=${autoSyncTriggeredRef.current}`);
    }
  }, [autoSyncOnMount, autoSyncDelay, startSync, dataType, user?.tx_import]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (progressPollIntervalRef.current) {
        clearInterval(progressPollIntervalRef.current);
        progressPollIntervalRef.current = null;
      }
    };
  }, []);

  return {
    syncStatus,
    syncProgress,
    isSyncing: syncStatus === 'running',
    syncType,
    startSync,
    stopSync,
    error
  };
}

